/* Crannóg Ales beer locator.
   Expects:
   - data-source: URL of crannog-locations.json
   - data-fresh-days: "recently stocked" badge window (0 = off)
   - data-max-age-days: hide locations with no delivery in N days (0 = show all)
   Leaflet (window.L) must be loaded before this element connects. */

class CrannogLocator extends HTMLElement {
  connectedCallback() {
    this.mapEl = this.querySelector('[data-map]');
    this.listEl = this.querySelector('[data-list]');
    this.countEl = this.querySelector('[data-count]');
    this.searchEl = this.querySelector('[data-search]');
    this.nearBtn = this.querySelector('[data-near]');
    this.toggleBtns = Array.from(this.querySelectorAll('[data-filter]'));

    this.filter = 'all';
    this.query = '';
    this.origin = null; // {lat,lng} when "near me" is active
    this.markers = new Map();
    this.activeId = null;

    this.freshDays = parseInt(this.dataset.freshDays || '0', 10);
    this.maxAgeDays = parseInt(this.dataset.maxAgeDays || '0', 10);

    fetch(this.dataset.source)
      .then((r) => r.json())
      .then((data) => this.init(data))
      .catch(() => {
        this.listEl.innerHTML =
          '<li class="crannog-locator__empty">Sorry, the locator failed to load. Please refresh the page.</li>';
      });
  }

  init(data) {
    const now = Date.now();
    this.locations = (data.locations || [])
      .filter((l) => l.lat != null)
      .filter((l) => {
        if (!this.maxAgeDays || !l.lastDelivery) return true;
        const age = (now - Date.parse(l.lastDelivery)) / 864e5;
        return age <= this.maxAgeDays;
      })
      .map((l, i) => Object.assign({ id: 'loc-' + i }, l));

    this.map = L.map(this.mapEl, { scrollWheelZoom: false });
    this.map.setView([50.3, -119.6], 6); // BC interior fallback view
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(this.map);

    this.locations.forEach((loc) => {
      const marker = L.marker([loc.lat, loc.lng], {
        icon: this.pinIcon(loc.type),
        title: loc.name
      });
      marker.bindPopup(this.popupHtml(loc));
      marker.on('click', () => this.setActive(loc.id));
      this.markers.set(loc.id, marker);
    });

    this.searchEl.addEventListener('input', () => {
      this.query = this.searchEl.value.trim().toLowerCase();
      this.render();
    });

    this.toggleBtns.forEach((btn) =>
      btn.addEventListener('click', () => {
        this.filter = btn.dataset.filter;
        this.toggleBtns.forEach((b) =>
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false')
        );
        this.render();
      })
    );

    if (this.nearBtn) {
      this.nearBtn.addEventListener('click', () => this.nearMe());
    }

    this.render({ fitAll: true });
  }

  visible() {
    let list = this.locations.filter(
      (l) => this.filter === 'all' || l.type === this.filter
    );
    if (this.query) {
      list = list.filter(
        (l) =>
          l.name.toLowerCase().includes(this.query) ||
          l.city.toLowerCase().includes(this.query) ||
          (l.region || '').toLowerCase().includes(this.query)
      );
    }
    if (this.origin) {
      list = list
        .map((l) => Object.assign({ dist: this.km(this.origin, l) }, l))
        .sort((a, b) => a.dist - b.dist);
    } else {
      list = list.sort(
        (a, b) => a.city.localeCompare(b.city) || a.name.localeCompare(b.name)
      );
    }
    return list;
  }

  render(opts = {}) {
    const list = this.visible();
    const ids = new Set(list.map((l) => l.id));

    this.markers.forEach((marker, id) => {
      if (ids.has(id)) {
        if (!this.map.hasLayer(marker)) marker.addTo(this.map);
      } else if (this.map.hasLayer(marker)) {
        this.map.removeLayer(marker);
      }
    });

    this.listEl.innerHTML = '';
    if (!list.length) {
      this.listEl.innerHTML =
        '<li class="crannog-locator__empty">No spots match — try a different search or filter.</li>';
    }
    list.forEach((loc) => {
      const li = document.createElement('li');
      li.className = 'crannog-locator__card';
      li.tabIndex = 0;
      li.dataset.id = loc.id;
      li.innerHTML =
        '<div class="crannog-locator__card-top"><div>' +
        '<h3 class="crannog-locator__card-name">' + this.esc(loc.name) + '</h3>' +
        '<p class="crannog-locator__card-city">' +
        this.esc(loc.region ? loc.city + ' · ' + loc.region : loc.city) +
        (loc.dist != null ? ' · ' + Math.round(loc.dist) + ' km away' : '') +
        '</p></div></div>' +
        '<div class="crannog-locator__badges">' +
        this.typeBadge(loc.type) +
        this.freshBadge(loc) +
        '<a class="crannog-locator__card-link" target="_blank" rel="noopener" href="' +
        this.mapsUrl(loc) + '">Directions</a>' +
        '</div>';
      const focus = () => this.setActive(loc.id, { pan: true });
      li.addEventListener('click', (e) => {
        if (e.target.closest('a')) return;
        focus();
      });
      li.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          focus();
        }
      });
      this.listEl.appendChild(li);
    });

    this.countEl.textContent =
      list.length + ' spot' + (list.length === 1 ? '' : 's') +
      (this.origin ? ', nearest first' : '');

    if (opts.fitAll && list.length) {
      const bounds = L.latLngBounds(list.map((l) => [l.lat, l.lng]));
      requestAnimationFrame(() => {
        this.map.invalidateSize();
        this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
      });
    }
  }

  setActive(id, opts = {}) {
    this.activeId = id;
    this.listEl.querySelectorAll('.crannog-locator__card').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.id === id);
    });
    this.markers.forEach((m, mid) => {
      const el = m.getElement();
      if (el) el.classList.toggle('is-active', mid === id);
    });
    const marker = this.markers.get(id);
    if (marker) {
      if (opts.pan) {
        this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 13), {
          animate: true
        });
        marker.openPopup();
        this.mapEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      if (opts.scrollList) {
        const card = this.listEl.querySelector('[data-id="' + id + '"]');
        if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  nearMe() {
    if (!navigator.geolocation) return;
    this.nearBtn.disabled = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        this.nearBtn.disabled = false;
        this.nearBtn.classList.add('is-active');
        this.render();
        const nearest = this.visible()[0];
        if (nearest) {
          this.map.setView([nearest.lat, nearest.lng], 11, { animate: true });
        }
      },
      () => {
        this.nearBtn.disabled = false;
        this.nearBtn.textContent = 'Location unavailable';
      }
    );
  }

  km(a, b) {
    const rad = Math.PI / 180;
    const x =
      Math.sin(((b.lat - a.lat) * rad) / 2) ** 2 +
      Math.cos(a.lat * rad) *
        Math.cos(b.lat * rad) *
        Math.sin(((b.lng - a.lng) * rad) / 2) ** 2;
    return 6371 * 2 * Math.asin(Math.sqrt(x));
  }

  pinIcon(type) {
    const fill = type === 'store' ? '#C98A2E' : '#1D3016';
    return L.divIcon({
      className: 'crannog-pin',
      iconSize: [30, 40],
      iconAnchor: [15, 40],
      popupAnchor: [0, -36],
      html:
        '<svg viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 25 15 25S30 25.5 30 15C30 6.7 23.3 0 15 0z" fill="' +
        fill + '"/><circle cx="15" cy="14.5" r="6" fill="#FBFAF3"/></svg>'
    });
  }

  popupHtml(loc) {
    return (
      '<div class="crannog-popup"><h4>' + this.esc(loc.name) + '</h4><p>' +
      this.esc(loc.city) +
      (loc.type === 'store' ? ' · Cans' : ' · On tap / dining') +
      (loc.precision === 'city' ? '<br>Approximate location — use directions' : '') +
      '</p><a target="_blank" rel="noopener" href="' + this.mapsUrl(loc) +
      '">Get directions →</a></div>'
    );
  }

  mapsUrl(loc) {
    return (
      'https://www.google.com/maps/search/?api=1&query=' +
      encodeURIComponent(loc.name + ', ' + loc.city + ', BC')
    );
  }

  typeBadge(type) {
    return type === 'store'
      ? '<span class="crannog-locator__badge crannog-locator__badge--store">Cans</span>'
      : '<span class="crannog-locator__badge crannog-locator__badge--restaurant">On tap / dining</span>';
  }

  freshBadge(loc) {
    if (!this.freshDays || !loc.lastDelivery) return '';
    const age = (Date.now() - Date.parse(loc.lastDelivery)) / 864e5;
    if (age > this.freshDays) return '';
    return '<span class="crannog-locator__badge crannog-locator__badge--fresh">Recently stocked</span>';
  }

  esc(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);
  }
}

customElements.define('crannog-locator', CrannogLocator);
